import type { Composition } from '@dashboard/contract';
import { useState } from 'react';
import { fmtM, monthLabel } from '../lib/format';

const W = 980;
const H = 320;
const PL = 56;
const PR = 14;
const PT = 16;
const PB = 30;

/** The demo's stacked area: each Top N song its own band in one hue stepped dark→light, grey "other" bands. */
export function CompositionChart({ c, mode }: { c: Composition; mode: 'abs' | 'pct' }) {
  const [hover, setHover] = useState<number | null>(null);
  const { months, bands, totals } = c;
  const songs = bands.filter((b) => b.kind === 'track');
  const N = songs.length;
  const color = (i: number, kind: string) =>
    kind === 'otherCatalogue'
      ? 'var(--g1)'
      : kind === 'otherNew'
        ? 'var(--g2)'
        : kind === 'otherUnknown'
          ? 'var(--g3)'
          : N < 2
            ? 'var(--e5)'
            : `color-mix(in oklab, var(--e5) ${(100 - (i * 100) / (N - 1)).toFixed(1)}%, var(--e1))`;
  const pct = mode === 'pct';
  const val = (b: Composition['bands'][number], i: number) =>
    pct ? (totals[i] ? ((b.values[i] ?? 0) / (totals[i] as number)) * 100 : 0) : (b.values[i] ?? 0);
  const max = pct ? 100 : Math.max(...totals) * 1.06 || 1;
  const x = (i: number) => PL + (i * (W - PL - PR)) / Math.max(months.length - 1, 1);
  const y = (v: number) => PT + (H - PT - PB) * (1 - v / max);

  const lower = new Array<number>(months.length).fill(0);
  const paths = bands.map((b, k) => {
    const upper = lower.map((v, i) => v + val(b, i));
    const top = upper
      .map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`)
      .join(' ');
    const bot = lower
      .map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`)
      .reverse()
      .join(' L');
    for (let i = 0; i < lower.length; i++) lower[i] = upper[i] ?? 0;
    return { key: b.key, d: `${top} L${bot} Z`, fill: color(k, b.kind) };
  });
  const steps = pct ? [0, 25, 50, 75, 100] : [0, 1, 2, 3].map((k) => (max * k) / 3);
  const colW = (W - PL - PR) / months.length;

  const i = hover;
  const tipSongs =
    i === null
      ? []
      : songs
          .filter((b) => (b.values[i] ?? 0) > 0)
          .sort((a, b2) => (b2.values[i] ?? 0) - (a.values[i] ?? 0))
          .slice(0, 5);
  const others = bands.filter((b) => b.kind !== 'track');
  const line = (b: Composition['bands'][number], idx: number) => (
    <div key={b.key}>
      <i style={{ background: color(bands.indexOf(b), b.kind) }} />
      {b.label}
      <b style={{ marginLeft: 6 }}>{fmtM(b.values[idx])}M</b>{' '}
      <span style={{ color: 'var(--muted)' }}>
        {totals[idx] ? (((b.values[idx] ?? 0) / (totals[idx] as number)) * 100).toFixed(0) : 0}%
      </span>
    </div>
  );

  return (
    <div className="card" style={{ padding: '14px 16px 10px' }}>
      {/* biome-ignore lint/a11y/noStaticElementInteractions: hover only reveals a tooltip; every number is also in the legend and the daily export */}
      <div className="stackwrap" onMouseLeave={() => setHover(null)}>
        <svg
          viewBox={`0 0 ${W} ${H}`}
          role="img"
          aria-label="Monthly worldwide streams split by top songs, other catalogue, other releases and releases without a date"
        >
          {steps.map((v) => (
            <g key={v}>
              <line
                x1={PL}
                y1={y(v).toFixed(1)}
                x2={W - PR}
                y2={y(v).toFixed(1)}
                stroke="var(--line)"
                strokeWidth="1"
              />
              <text
                x={PL - 9}
                y={(y(v) + 4).toFixed(1)}
                fontSize="11.5"
                fontFamily="var(--mono)"
                fill="var(--muted)"
                textAnchor="end"
              >
                {pct ? `${v}%` : `${fmtM(v)}M`}
              </text>
            </g>
          ))}
          {paths.map((p) => (
            <path
              key={p.key}
              d={p.d}
              fill={p.fill}
              stroke="var(--surface)"
              strokeWidth={bands.length > 6 ? 0.5 : 1}
            />
          ))}
          {months.map((m, idx) =>
            m.endsWith('-01') ? (
              <g key={m}>
                <line
                  x1={x(idx).toFixed(1)}
                  y1={PT}
                  x2={x(idx).toFixed(1)}
                  y2={H - PB}
                  stroke="var(--surface)"
                  strokeWidth="1"
                  opacity=".45"
                />
                <text
                  x={x(idx).toFixed(1)}
                  y={H - 10}
                  fontSize="11.5"
                  fontFamily="var(--mono)"
                  fill="var(--muted)"
                  textAnchor="middle"
                >
                  {m.slice(0, 4)}
                </text>
              </g>
            ) : null,
          )}
          {i !== null ? (
            <line
              x1={x(i)}
              y1={PT}
              x2={x(i)}
              y2={H - PB}
              stroke="var(--ink)"
              strokeWidth="1"
              opacity=".3"
              pointerEvents="none"
            />
          ) : null}
          <line
            x1={PL}
            y1={H - PB}
            x2={W - PR}
            y2={H - PB}
            stroke="var(--line-strong)"
            strokeWidth="1"
          />
          {months.map((m, idx) => (
            // biome-ignore lint/a11y/noStaticElementInteractions: hover only reveals a tooltip
            <rect
              key={m}
              x={(x(idx) - colW / 2).toFixed(1)}
              y={PT}
              width={colW.toFixed(1)}
              height={H - PT - PB}
              fill="transparent"
              onMouseEnter={() => setHover(idx)}
            />
          ))}
        </svg>
        {i !== null ? (
          <div
            className="tip"
            style={{
              left: `min(max(${(x(i) / W) * 100}% + 12px, 4px), calc(100% - 260px))`,
              top: 8,
            }}
          >
            <span className="h">
              {monthLabel(months[i] as string)} · {fmtM(totals[i])}M
              {c.partialLastMonth && i === months.length - 1 ? ' · partial month' : ''}
            </span>
            {tipSongs.map((b) => line(b, i))}
            {songs.length > tipSongs.length ? (
              <div style={{ color: 'var(--muted)' }}>
                … {songs.length - tipSongs.length} more of the top {N}
              </div>
            ) : null}
            {others.map((b) => line(b, i))}
          </div>
        ) : null}
      </div>
      <div className="slegend">
        {bands.map((b, k) => (
          <span key={b.key} title={b.label}>
            <i style={{ background: color(k, b.kind) }} />
            {b.label}
          </span>
        ))}
      </div>
    </div>
  );
}
